import Vue from 'vue'
import Component from 'vue-class-component'
import pick from 'lodash/pick'
import isString from 'lodash/isString'
import isArray from 'lodash/isArray'
import flatten from 'lodash/flatten'

import { isObject, valIsMissing } from '@/utils/data'

@Component
export class RouteMixin extends Vue {
  /**
   * Utility method for getting a copy of the current route for modifying to
   * set a new route in router
   */
  // eslint-disable-next-line
  $_RouteMixin_getRouteCopy () {
    return pick(this.$route, ['hash', 'name', 'params', 'query'])
  }
  // eslint-disable-next-line
  $_RouteMixin_setRoute (route) {
    this.$router.push(route)
  }
}

@Component
export class RootSizeMixin extends Vue {
  /**
   * If this component has the root element set with a ref="root", this will
   * get the sizes on load
   */
  inner = { w: 0, h: 0 }
  outer = { w: 0, h: 0 }
  mounted () {
    this.$_RootSizeMixin_updateInnerSize()
    this.$_RootSizeMixin_updateOuterSize()
  }

  /* Methods - utility */

  // Force getting size, without caching
  // eslint-disable-next-line
  $_RootSizeMixin_calcInnerSize () {
    const c = this.$refs.root
    const s = { w: 0, h: 0 }
    if (c) {
      s.w = Math.max(0, c.getBoundingClientRect().width - getComputedStyle(c).borderLeftWidth.slice(0, -2) - getComputedStyle(c).borderRightWidth.slice(0, -2))
      s.h = Math.max(0, c.getBoundingClientRect().height - getComputedStyle(c).borderTopWidth.slice(0, -2) - getComputedStyle(c).borderTopWidth.slice(0, -2))
    }
    return s
  }
  // eslint-disable-next-line
  $_RootSizeMixin_calcOuterSize () {
    const c = this.$refs.root
    const s = { w: 0, h: 0 }
    if (c) {
      const r = c.getBoundingClientRect()
      s.w = r.width
      s.h = r.height
    }
    return s
  }

  // eslint-disable-next-line
  $_RootSizeMixin_updateInnerSize () {
    Object.assign(this.inner, this.$_RootSizeMixin_calcInnerSize())
  }
  // eslint-disable-next-line
  $_RootSizeMixin_updateOuterSize () {
    Object.assign(this.outer, this.$_RootSizeMixin_calcOuterSize())
  }
}

@Component({
  props: {
    isCalculatingLayout: {
      type: Boolean,
      default: false
    }
  }
})
export class LayoutMixin extends Vue {
  /**
   * Utility method for class to hide with display:none or visibility:hidden
   *  based on if it needs to calculate sizes for layout
   * isCalculatingLayout flag should be passed in from outside to specify
   *  time period when layout is being updated
   */
  get hiddenClass () {
    return this.isCalculatingLayout ? 'calc-hidden' : 'no-calc-hidden'
  }
}

/*
* Utility method used by VizLegendDivs and VizCompBars to validate/check
*  and organize the legend data. VizLegendDivs is the current component
*  for adding legends. Used in VizCompBars for backwards compatibility of
*  previous years.
*  legend (optional - prop) - string or array of text to be displayed next to comp
*    bar in legend; will be hidden if blank/undefined.  Can also be an Object
*    { main: [], comp: [[]] } with main an array of strings for main bar, and
*    a 2D array of strings for multi-line texts for each of the comp bars.
*  legendObject - output is similar to { main: [], comp: [[]] }
*  hasLegend - flattens main and comp arrays in legendObject (if needed) and
*    checks if the values within are not empty strings and are of string type.
*/
@Component({
  props: {
    legend: [String, Array, Object]
  }
})
export class LegendObjectMixin extends Vue {
  /* Legend */
  get legendObject () {
    const t = this.legend
    const result = {
      main: [],
      comp: []
    }
    if (valIsMissing(t)) {
      // do nothing if no legend passed
    } else if (isString(t)) {
      result.comp = [[t]]
    } else if (isArray(t)) {
      result.comp = [t]
    } else if (isObject(t)) {
      // Assuming main is 1D array and converting to 2D array to be similar to comp
      result.main = [t.main]
      result.comp = t.comp
    } else {
      console.log('Warning: Unknown input type in legend object')
    }

    if (this.legendArrayHasInvalidValues(result.main) || this.legendArrayHasInvalidValues(result.comp)) {
      console.log('Warning: Invalid values in legend object')
    }

    return result
  }

  get hasLegend () {
    return flatten(this.flattenedLegendItems).some(d => isString(d) && !valIsMissing(d))
  }

  legendArrayHasInvalidValues (d) {
    return flatten(d).some(e => !isString(e))
  }

  get flattenedLegendItems () {
    return this.legendObject.main.concat(this.legendObject.comp)
  }
}

@Component
export class TabNavMixin extends Vue {
  /**
    Utility method for FrameworkRatings and ReportMenu when ratings items are clicked; URL-hash is updated and the app scrolls smoothly down to correct section when in mobile/tablet view.
   */
  navigateToTab (hash) {
    window.location.hash = '#' + hash
    const spacing = 7

    const el = document.getElementById(hash)
    const pos = el.offsetTop + spacing

    const browserSupportsScroll = 'scrollBehavior' in document.documentElement.style
    if (window.innerWidth < 957) {
      if (browserSupportsScroll) {
        window.scrollTo({
          top: pos,
          left: 0,
          behavior: 'smooth'
        })
      } else {
        // IE doesn't do smooth scrolling, so use plain method,
        //  so it doesn't jump to top of page
        window.scrollTo(0, pos)
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/mixin.js